import { ScreenBreakpoints } from 'src/hooks/useBreakpoints';
import { GetWalkmeDataFunc, WalkmeModel, WalkmeName } from 'src/types/walkme.model';
import WalkmeDataMap from 'src/components/atomic/organisms/walkme/WalkmeDataMap';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import DateUtils from './date';
import ObjectUtils from './object';

// Currying happening here. Given a walkme name, get back a function that takes in breakpoints and returns a WalkmeModel.
const getWalkmeData =
    (walkmeName: WalkmeName): GetWalkmeDataFunc =>
    (breakpoints: ScreenBreakpoints): WalkmeModel => {
        if (WalkmeDataMap[walkmeName]) {
            return WalkmeDataMap[walkmeName](breakpoints);
        }
        console.log('Walkme data function not found for', walkmeName);
        return null;
    };

export default getWalkmeData;

export function isWalkmeFeatureFlagTimeDependent(data: WalkmeModel): boolean {
    return data?.featureFlagEnablementDependsOnDaysSinceReleaseDate || false;
}

export function isTodayOnOrAfterReleaseDate(
    data: WalkmeModel,
    featureFlagProperties: { [key: string]: string }
): boolean {
    if (!data || !featureFlagProperties) return false;

    const releaseDatePropertyName: string = data.featureFlagReleaseDatePropertyName;
    const releaseDate: LocalDate = DateUtils.parseStringToDate(featureFlagProperties[releaseDatePropertyName]);

    if (!releaseDate) return false;

    return LocalDate.now().isAfter(releaseDate) || LocalDate.now().isEqual(releaseDate);
}

export function isTodayInTheShowWalkmeTimeWindow(
    data: WalkmeModel,
    featureFlagProperties: { [key: string]: string }
): boolean {
    const releaseDatePropertyName: string = data.featureFlagReleaseDatePropertyName;
    const daysToShowAfterReleaseDatePropertyName: string = data.featureFlagDaysToShowAfterReleaseDatePropertyName;

    if (!featureFlagProperties || !releaseDatePropertyName || !daysToShowAfterReleaseDatePropertyName || !data) {
        console.log(
            'walkme: essential time window data is missing',
            releaseDatePropertyName,
            daysToShowAfterReleaseDatePropertyName,
            data ? 'data exists' : 'data is null'
        );
        return false;
    }

    const releaseDate: LocalDate = DateUtils.parseStringToDate(featureFlagProperties[releaseDatePropertyName]);
    const daysToShowAfterReleaseDate: number = ObjectUtils.parseStringToInt(
        featureFlagProperties[daysToShowAfterReleaseDatePropertyName]
    );

    if (!releaseDate || daysToShowAfterReleaseDate < 0) {
        console.log(
            'walkme: releaseDate or daysToShowAfterReleaseDate is null/negative, so feature is not enabled.',
            releaseDate,
            daysToShowAfterReleaseDate
        );
        return false;
    }

    if (releaseDate && daysToShowAfterReleaseDate >= 0) {
        const daysSinceReleaseDate: number = releaseDate.until(LocalDate.now(), ChronoUnit.DAYS);

        if (daysSinceReleaseDate < 0) {
            console.log('walkme: releaseDate is in the future, so feature is not enabled');
            return false;
        }
        const inTimeWindow: boolean = daysSinceReleaseDate <= daysToShowAfterReleaseDate;
        if (!inTimeWindow) {
            console.log('walkme: outside time window', daysSinceReleaseDate, daysToShowAfterReleaseDate);
            return false;
        } else {
            return true;
        }
    }

    return false;
}
